<template>
  <div>
    <div v-if="!chart && treeData.full_name" class="d-flex flex-wrap" :class="{ 'padding-left-box': level > 1 }">
      <div class="d-flex flex-wrap" :class="{ stripes: level > 0 && !last, 'full-width': level > 0 || (level === 0 && ((!overflow && !shipping) || (!overflowShipping && shipping))) }">
        <div class="d-flex full-width" :class="{ 'margin-top-negative': level > 0 }">
          <div v-if="level > 0" class="toggle-width position-relative">
            <div class="line"></div>
          </div>
          <div class="d-flex align-items-center full-width border-bottom overlay-box" :class="getBackgroundColor(treeData)">
            <div class="p-10 toggle-width" v-if="showData && (admin || (treeData.city && treeData.country) || level === 0)">
              <div>
                <font-awesome-icon v-if="!treeData.extend" icon="chevron-circle-right" class="red pointer" @click="toggleExtend(treeData)"></font-awesome-icon>
                <font-awesome-icon v-if="treeData.extend" icon="chevron-circle-down" class="red pointer" @click="toggleExtend(treeData)"></font-awesome-icon>
              </div>
            </div>
            <div class="full-width" :class="{ 'border-left': showData }">
              <div v-if="!(treeData.city && treeData.country)" class="border-bottom p-10">
                <div class="d-flex align-items-center">
                  <div class="font-weight-bold mr-2"><b-link :to="{ path: '/user/' + slugText(treeData.id + '-' + treeData.full_name) }" class="text-inherit">{{ treeData.full_name }}</b-link></div>
                  <div v-if="admin" class="d-flex align-items-center">
                    <b-form-input v-if="locked && delivered && !treeData.delivered && shipping && !treeData.shipped && level > 0" type="text" size="sm"
                                  v-model.trim="treeData.tracking" class="mr-2" :placeholder="$t('messages.tracking')" />
                    <click-confirm button-size="sm" :messages="confirmMessage" class="mr-2">
                      <b-button v-b-tooltip.hover :title="$t('messages.distribution_shipped')"
                                v-if="locked && delivered && !treeData.loading && !treeData.delivered && shipping && !treeData.shipped && level > 0"
                                variant="primary" size="sm" @click="ship">
                        <font-awesome-icon icon="shipping-fast" class="text-white"></font-awesome-icon>
                      </b-button>
                      <b-button v-b-tooltip.hover :title="$t('messages.distribution_delivered')"
                                v-if="((delivered && level > 1 && locked) || (level < 2 && !shipping) || (level < 2 && shipping && (locked || level === 0))) && !treeData.loading && !treeData.delivered && (!shipping || (shipping && treeData.shipped) || level === 0)"
                                variant="primary" size="sm" @click="deliver">
                        <font-awesome-icon icon="check" class="text-white"></font-awesome-icon>
                      </b-button>
                      <b-button v-if="treeData.loading" variant="primary" size="sm" :disabled="true">
                        <font-awesome-icon icon="circle-notch" class="text-white" spin></font-awesome-icon>
                      </b-button>
                    </click-confirm>
                  </div>
                </div>
                <div v-if="admin && level > 0 && !treeData.delivered && !treeData.shipped" class="d-flex align-items-center mt-2">
                  <b-form-select v-model="treeData.pickup" class="mr-2">
                    <option :value="{ type: 'send', id: null }">{{ $t('messages.send_ship') }}</option>
                    <optgroup :label="$t('messages.pickup')">
                      <option v-for="pickupPoint in pickupPoints" :key="'p-' + pickupPoint.id" :value="{ type: 'pickup', id: pickupPoint.id }" :disabled="isOwnPickupPoint(pickupPoint, treeData.id)">
                        {{ pickupPoint.city }}, {{ $t('countries.' + pickupPoint.country) }}{{ pickupPoint.extra_info ? ' (' + pickupPoint.extra_info + ')' : '' }}
                      </option>
                    </optgroup>
                    <optgroup :label="$t('messages.pickup_by')" v-if="pickupBy && pickupBy.length > 0">
                      <option v-for="participantPickupBy in pickupBy" :key="'u-' + participantPickupBy.id"
                              :value="{ type: 'user', id: participantPickupBy.id }" :disabled="participantPickupBy.id == treeData.id">{{ participantPickupBy.full_name }}</option>
                    </optgroup>
                  </b-form-select>
                  <b-button v-if="!treeData.loadingPickup" v-b-tooltip.hover :title="$t('messages.distribution_edit')" variant="primary" size="sm">
                    <font-awesome-icon icon="pencil-alt" class="text-white" @click="pickup"></font-awesome-icon>
                  </b-button>
                  <b-button v-if="treeData.loadingPickup" v-b-tooltip.hover :title="$t('messages.distribution_edit')" variant="primary" size="sm" :disabled="true">
                    <font-awesome-icon icon="circle-notch" class="text-white" spin></font-awesome-icon>
                  </b-button>
                </div>
              </div>
              <div v-if="treeData.city && treeData.country">
                <div class="p-10" :class="{ 'border-bottom': showData }">
                  <span class="font-weight-bold"><b-link :to="{ path: '/user/' + slugText(treeData.user_id + '-' + treeData.full_name) }" class="text-inherit">{{ treeData.full_name }}</b-link></span>
                  <p>
                    <span>{{ treeData.city }}, {{ $t('countries.' + treeData.country) }}</span>
                  </p>
                </div>
                <div v-if="showData" class="p-10">
                  <span class="font-weight-bold">{{ $t('messages.to_divide') }}:</span><br />
                  <p v-for="(product, index) in getAllProductsFromUsers(showData)" :key="'iud' + index">{{ product.amount }}x {{ product.name }}</p>
                </div>
              </div>
              <div v-if="(!shipping || (shipping && level > 0)) && getAllProductsFromUser(treeData).length > 0" class="p-10">
                <p v-for="(product, index) in getAllProductsFromUser(treeData)" :key="'iu' + index">{{ product.amount }}x {{ product.name }}</p>
              </div>
              <div v-if="(!shipping || (shipping && level > 0)) && showData && !(treeData.city && treeData.country) && (admin || level === 0)" class="p-10">
                <span class="font-weight-bold">{{ $t('messages.to_keep') }}:</span><br />
                <p v-for="(product, index) in treeData.products" :key="'i' + index">{{ product.amount }}x {{ product.name }}</p>
                <p v-if="!treeData.products || treeData.products.length === 0">{{ $t('messages.nothing') }}</p>
              </div>
              <div v-if="shipping && level === 0 && showData" class="p-10">
                <span class="font-weight-bold">{{ $t('messages.to_send') }}:</span><br />
                <p v-for="(product, index) in getAllProductsFromUsers(showData)" :key="'ius' + index">{{ product.amount }}x {{ product.name }}</p>
              </div>
            </div>
          </div>
        </div>
        <div v-if="showData && (admin || (treeData.city && treeData.country) || level === 0) && treeData.extend" v-for="(showDataItem, index) in showData" :key="index" class="full-width">
          <treeview :data="showDataItem" :chart="chart" :admin="admin" :level="level + 1" :last="index === (showData.length - 1)" :shipping="shipping && level < 1"
                    :pickup-points="pickupPoints" :pickup-by="pickupBy" :delivered="treeData.delivered" :locked="locked"
                    @deliver="$emit('deliver', $event)" @ship="$emit('ship', $event)" @pickup="$emit('pickup', $event)" />
        </div>
      </div>
    </div>
    <div v-if="chart && treeData.full_name">
      <table>
        <tr>
          <td :colspan="showData ? showData.length * 2 : 1"
              :class="{ extend: showData && treeData.extend && (admin || (treeData.city && treeData.country) || level === 0) }">
            <div class="node">
              <div class="person" @click="$emit('click-node', treeData)">
                <div class="avat">
                  <font-awesome-icon icon="user-circle" size="3x" class="red"></font-awesome-icon>
                </div>
                <div class="name">
                  <div class="font-weight-bold">{{ treeData.full_name }}</div>
                  <div v-if="(!shipping || (shipping && level > 0)) && treeData.products && treeData.products.length > 0">
                    <p v-for="(product, index) in getAllProductsFromUser(treeData)" :key="'iu' + index">{{ product.amount }}x {{ product.name }}</p>
                    <div v-if="showData && (admin || level === 0)">
                      <span class="font-weight-bold">{{ $t('messages.to_keep') }}:</span><br />
                      <p v-for="(product, index) in treeData.products" :key="'i' + index">{{ product.amount }}x {{ product.name }}</p>
                    </div>
                  </div>
                  <div v-if="treeData.city && treeData.country">
                    <p>
                      <span>{{ treeData.city }}, {{ $t('countries.' + treeData.country) }}</span>
                    </p>
                    <div v-if="showData">
                      <span class="font-weight-bold">{{ $t('messages.to_divide') }}:</span><br />
                      <p v-for="(product, index) in getAllProductsFromUsers(showData)" :key="'iud' + index">{{ product.amount }}x {{ product.name }}</p>
                    </div>
                  </div>
                  <div v-if="shipping && level === 0 && showData">
                    <span class="font-weight-bold">{{ $t('messages.to_send') }}:</span><br />
                    <p v-for="(product, index) in getAllProductsFromUsers(showData)" :key="'ius' + index">{{ product.amount }}x {{ product.name }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="extend_handle" v-if="showData && (admin || (treeData.city && treeData.country) || level === 0)" @click="toggleExtend(treeData)"></div>
          </td>
        </tr>
        <tr v-if="showData && (admin || (treeData.city && treeData.country) || level === 0) && treeData.extend">
          <td v-for="(showDataItem, index) in showData" :key="index" colspan="2" class="childLevel">
            <treeview :data="showDataItem" :chart="chart" :admin="admin" :level="level + 1" :locked="locked" :shipping="shipping" @click-node="$emit('click-node', $event)"/>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import { defaultMixin } from '../../mixin/default'
import { productMixin } from '../../mixin/product'

export default {
  name: 'treeview',
  props: {
    data: {
      required: true
    },
    pickupPoints: {
      required: false
    },
    pickupBy: {
      required: false
    },
    level: {
      type: Number,
      required: false,
      default: 0
    },
    shipping: {
      type: Boolean,
      required: false,
      default: false
    },
    delivered: {
      type: Boolean,
      required: false,
      default: false
    },
    last: {
      type: Boolean,
      required: false,
      default: false
    },
    chart: {
      type: Boolean,
      required: false,
      default: false
    },
    admin: {
      type: Boolean,
      required: false,
      default: false
    },
    locked: {
      type: Boolean,
      required: true
    }
  },
  data: function () {
    return {
      treeData: {},
      overflow: this.calculateOverflow(),
      overflowShipping: this.calculateOverflowShipping()
    }
  },
  mixins: [ defaultMixin, productMixin ],
  computed: {
    showData: function () {
      if (this.shipping && this.level === 0) {
        if (this.treeData.shipments && this.treeData.shipments.length > 0) {
          return this.treeData.shipments
        } else {
          return false
        }
      } else {
        if (this.treeData.pickups && this.treeData.pickups.length > 0) {
          return this.treeData.pickups
        } else if (this.treeData.users && this.treeData.users.length > 0) {
          return this.treeData.users
        } else {
          return false
        }
      }
    },
    confirmMessage: function () {
      return {
        title: this.$i18n.t('messages.are_you_sure'),
        yes: this.$i18n.t('messages.yes'),
        no: this.$i18n.t('messages.no')
      }
    }
  },
  watch: {
    data: {
      handler: function (properties) {
        let extendKey = function (jsonData) {
          if (jsonData.extend === undefined || jsonData.extend === null) {
            jsonData.extend = true
          }
          jsonData.loading = false
          jsonData.loadingPickup = false
          return jsonData
        }
        if (properties) {
          this.treeData = extendKey(properties)
        }
      },
      immediate: true
    }
  },
  methods: {
    toggleExtend: function (treeData) {
      treeData.extend = !treeData.extend
      this.$forceUpdate()
    },
    getAmount: function (products) {
      let amount = 0
      products.forEach(function (product) {
        amount += product.amount
      })
      return amount
    },
    getBackgroundColor: function (user) {
      let classes = ''
      if (this.level > 0) {
        classes = classes.concat('border-left border-top')
      }
      if (user.shipped && !user.delivered) {
        classes = classes.concat(' bg-warning text-white')
      } else if (user.delivered) {
        classes = classes.concat(' bg-success text-white')
      }
      return classes
    },
    isOwnPickupPoint: function (pickupPoint, userId) {
      return parseInt(pickupPoint.user_id) === parseInt(userId)
    },
    ship: function () {
      this.treeData.loading = true
      this.$emit('ship', this.treeData)
      this.$forceUpdate()
    },
    deliver: function () {
      this.treeData.loading = true
      this.$emit('deliver', this.treeData)
      this.$forceUpdate()
    },
    pickup: function () {
      this.treeData.loadingPickup = true
      this.$emit('pickup', this.treeData)
      this.$forceUpdate()
    },
    calculateOverflow: function () {
      var element = this.$parent.$parent.$refs.distributionBox
      if (element) {
        return element.offsetWidth < element.scrollWidth
      } else {
        return false
      }
    },
    calculateOverflowShipping: function () {
      var element = this.$parent.$parent.$refs.shippingBox
      if (element) {
        return element.offsetWidth < element.scrollWidth
      } else {
        return false
      }
    }
  },
  updated: function () {
    if (!this.shipping) {
      let overflowCalc = this.calculateOverflow()
      if (this.overflow !== overflowCalc) {
        this.overflow = overflowCalc
      }
    } else {
      let overflowCalc = this.calculateOverflowShipping()
      if (this.overflowShipping !== overflowCalc) {
        this.overflowShipping = overflowCalc
      }
    }
  }
}
</script>

<style scoped>
  table {
    border-collapse: separate !important;
    border-spacing: 0px !important;
  }

  td {
    position: relative;
    vertical-align: top;
    padding: 0px 0px 50px;
    text-align: center;
  }

  .extend_handle {
    position: absolute;
    left: 50%;
    bottom: 30px;
    width: 10px;
    height: 10px;
    margin-left: 10px;
    transform: translate3d(-15px, 0px, 0px);
    cursor: pointer;
  }

  .extend_handle:before {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border: 2px solid;
    border-color: #ccc #ccc transparent transparent;
    transform: rotateZ(135deg);
    transform-origin: 50% 50% 0px;
    transition: transform ease 300ms;
  }

  .extend_handle:hover:before {
    border-color:#333 #333 transparent transparent;
  }

  .extend .extend_handle:before {
    transform: rotateZ(-45deg);
  }

  .extend::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 15px;
    height: 15px;
    border-left: 2px solid #ccc;
    transform: translate3d(-1px, 0px, 0px)
  }

  .childLevel::before {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 100%;
    height: 15px;
    border-left: 2px solid #ccc;
    transform: translate3d(-1px, 0px, 0px)
  }

  .childLevel::after {
    content: "";
    position: absolute;
    left: 0px;
    right: 0px;
    top: -15px;
    border-top: 2px solid #ccc;
  }

  .childLevel:first-child:before, .childLevel:last-child:before {
    display: none;
  }

  .childLevel:first-child:after {
    left: 50%;
    height: 15px;
    border: 2px solid;
    border-color: #ccc transparent transparent #ccc;
    border-radius: 6px 0px 0px 0px;
    transform: translate3d(1px, 0px, 0px)
  }

  .childLevel:last-child:after {
    right: 50%;
    height: 15px;
    border: 2px solid;
    border-color: #ccc #ccc transparent transparent;
    border-radius: 0px 6px 0px 0px;
    transform: translate3d(-1px, 0px, 0px)
  }

  .childLevel:first-child.childLevel:last-child::after {
    left: auto;
    border-radius: 0px;
    border-color: transparent #ccc transparent transparent;
    transform: translate3d(1px, 0px, 0px)
  }

  .node {
    position: relative;
    display: inline-block;
    width: 13em;
    box-sizing: border-box;
    text-align: center;
  }

  .node .person {
    position: relative;
    display: inline-block;
    z-index: 2;
    width: 150px;
  }

  .node .person .avat {
    display: block;
    width: 4em;
    height: 4em;
    margin: auto;
    background:#fff;
    box-sizing: border-box;
  }

  .node .person .avat img {
    width: 100%;
    height: 100%;
  }

  .toggle-width {
    min-width: 35px;
  }

  .border-left {
    border-left: 1px #D3D3D3 solid;
  }

  .border-top {
    border-top: 1px #D3D3D3 solid;
  }

  .border-bottom {
    border-bottom: 1px #D3D3D3 solid;
  }

  .margin-top-negative {
    margin-top: -1px;
  }

  .overlay-box {
    z-index: 1;
    background: #FFFFFF;
    min-width: 200px;
  }

  .padding-left-box {
    padding-left: 36px;
  }

  .stripes {
    background-image: -webkit-linear-gradient(90deg, transparent 17px, #cccccc 17px, #cccccc 19px, transparent 19px, transparent 100%);
    background-image: -moz-linear-gradient(90deg, transparent 17px, #cccccc 17px, #cccccc 19px, transparent 19px, transparent 100%);
    background-image: -o-linear-gradient(90deg, transparent 17px, #cccccc 17px, #cccccc 19px, transparent 19px, transparent 100%);
    background-image: -ms-linear-gradient(90deg, transparent 17px, #cccccc 17px, #cccccc 19px, transparent 19px, transparent 100%);
    background-image: linear-gradient(90deg, transparent 17px, #cccccc 17px, #cccccc 19px, transparent 19px, transparent 100%);
  }

  .line {
    position: relative;
    top: -50%;
    left: 17px;
    height: 100%;
    overflow: hidden;
    border: 2px solid;
    border-color: #CCCCCC transparent transparent #CCCCCC;
    border-radius: 10px 0px 0px 0px;
    transform: scaleY(-1);
  }
</style>



// WEBPACK FOOTER //
// src/components/project/TreeView.vue